import { CartLineEdge, CartLineNode } from '../shopify';
import { convertRubyHashToJson } from '@/hooks/usePlayGeniusCheckout/utils/rubyHashToJson';

/**
 * Calculates subtotal for a cart line item
 */
export const calculateSubtotal = (
  priceAmount: string | number,
  quantity: number,
) => {
  return parseFloat(String(priceAmount)) * quantity;
};

/**
 * Updates a cart line with new quantity and recalculates subtotal
 */
export const updateCartLineQuantity = (
  edge: CartLineEdge,
  newQuantity: number,
) => ({
  ...edge,
  node: {
    ...edge.node,
    quantity: newQuantity,
    cost: {
      ...edge.node.cost,
      subtotalAmount: {
        ...edge.node.cost.subtotalAmount,
        amount: calculateSubtotal(
          edge.node.merchandise.price?.amount || '0',
          newQuantity,
        ),
      },
    },
  },
});

/**
 * Calculates total quantity from cart line edges
 */
export const calculateTotalQuantity = (edges: any[]) =>
  edges.reduce((total, edge) => total + edge.node.quantity, 0);

/**
 * Creates a Map from attributes array for O(1) lookups
 * @param attributes - Array of attribute objects
 * @returns Map with key-value pairs
 */
export function createAttributesMap(
  attributes: Array<{ key: string; value: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const attr of attributes) {
    map.set(attr.key, attr.value);
  }
  return map;
}

/**
 * Creates a getProperty function that checks both regular and underscore-prefixed keys
 * @param attributesMap - Map of attributes
 * @returns Function that returns the value for a key (checking both key and _key)
 */
export function createGetPropertyFunction(
  attributesMap: Map<string, string>,
): (key: string) => string | undefined {
  return (key: string): string | undefined => {
    return attributesMap.get(key) || attributesMap.get(`_${key}`);
  };
}

/**
 * Calculate the display price for a cart item based on complex business logic
 * This replicates the liquid template price logic
 */
export function calculateDisplayPrice(node: CartLineNode): {
  price: string;
  currency: string;
  savings?: string;
} {
  const attributes = node.attributes || [];
  const productHandle = node.merchandise.product?.handle;
  const currencyCode = node.merchandise.price?.currencyCode || 'USD';

  // Create attributes map for O(1) lookups
  const attributesMap = createAttributesMap(attributes);

  // Extract properties from attributes
  const getProperty = createGetPropertyFunction(attributesMap);

  // Check item types
  const isBookBundle = getProperty('is_book_bundle') === 'true';
  const priceSource = getProperty('price_source');
  const displayPrice = getProperty('display_price');
  const price = getProperty('price');

  // Subscription bundle handles
  const playgeniusPlayKitBundle = 'subscription-1';
  const playgeniusGiftBundle = 'subscription-gifting';

  // Handle subscription payload for Play Kit Bundle
  if (productHandle === playgeniusPlayKitBundle) {
    const subscriptionsPayload = getProperty('subscriptionsPayload');

    if (subscriptionsPayload) {
      try {
        // Convert Ruby hash syntax to valid JSON
        const jsonString = convertRubyHashToJson(subscriptionsPayload);

        const payload = JSON.parse(jsonString);

        const bundleDiscountedPrice = payload.bundleDiscountedPrice;

        if (
          bundleDiscountedPrice !== undefined &&
          bundleDiscountedPrice !== null
        ) {
          const calculatedPrice = (bundleDiscountedPrice / 100).toFixed(2);
          return {
            price: calculatedPrice, // Convert from cents
            currency: currencyCode,
            savings:
              payload.bundle?.savings !== undefined &&
              payload.bundle?.savings !== null
                ? (payload.bundle.savings / 100).toFixed(2)
                : undefined,
          };
        }
      } catch (e) {
        console.warn('Failed to parse subscriptionsPayload:', e);
      }
    }
  }

  // Handle book bundles
  if (isBookBundle && price) {
    return {
      price: (Number(price) / 100).toFixed(2),
      currency: currencyCode,
    };
  }

  // Handle gift bundles
  if (productHandle === playgeniusGiftBundle) {
    const gifts = getProperty('gifts');

    if (gifts) {
      try {
        // Convert Ruby hash syntax to valid JSON
        const jsonString = convertRubyHashToJson(gifts);
        const giftsData = JSON.parse(jsonString);
        const bundlePrice = giftsData.bundle?.price;
        if (bundlePrice) {
          return {
            price: (parseInt(bundlePrice) / 100).toFixed(2), // Convert from cents
            currency: currencyCode,
          };
        }
      } catch (e) {
        console.warn('Failed to parse gifts data:', e);
      }
    }
  }

  // Handle items with price_source and display_price
  if (priceSource && displayPrice) {
    const unitPrice = parseFloat(displayPrice).toFixed(2);
    return {
      price: unitPrice,
      currency: currencyCode,
    };
  }

  // Default: use individual item price, fallback to subtotal if individual price is missing
  const individualPrice = node.merchandise.price?.amount;
  const fallbackPrice =
    individualPrice || String(node.cost.subtotalAmount.amount);

  return {
    price: String(fallbackPrice),
    currency:
      node.merchandise.price?.currencyCode ||
      node.cost.subtotalAmount.currencyCode,
  };
}

/**
 * Determines if a potential addon node is actually an addon for the given parent node
 */
export function isAddonForParent(
  potentialAddon: CartLineNode,
  parentNode: CartLineNode,
  _allEdges: { node: CartLineNode }[],
): boolean {
  if (potentialAddon.id === parentNode.id) return false; // Skip self

  const addonAttributes = potentialAddon.attributes || [];
  const addonTitle = potentialAddon.merchandise.product?.title || '';
  const parentAttributes = parentNode.attributes || [];

  // Create attributes maps for O(1) lookups
  const addonAttributesMap = createAttributesMap(addonAttributes);
  const parentAttributesMap = createAttributesMap(parentAttributes);

  // Create getProperty functions for centralized attribute lookup
  const getAddonProperty = createGetPropertyFunction(addonAttributesMap);
  const getParentProperty = createGetPropertyFunction(parentAttributesMap);

  // Priority 1: Check for explicit parent_product_id attribute (highest priority)
  const parentProductId = getAddonProperty('parent_product_id');
  if (parentProductId) {
    // If parent_product_id exists, it must match exactly or return false
    return parentProductId === parentNode.merchandise.id;
  }

  // Priority 2: Only use legacy logic if no parent_product_id exists
  const isCoursePack = getParentProperty('is_course_pack') === 'true';
  const isSubscription =
    getParentProperty('is_playgenius_subscription') === 'true';

  if (!isCoursePack && !isSubscription) return false;

  if (isCoursePack) {
    // For course packs, look for book bundles with linked_product_handle matching parent handle
    const linkedProductHandle = getAddonProperty('linked_product_handle');
    const isBookBundle =
      addonTitle.includes('Book Bundle') ||
      potentialAddon.merchandise.product?.handle?.includes('book-bundle');

    return (
      !!linkedProductHandle &&
      linkedProductHandle === parentNode.merchandise.product?.handle &&
      Boolean(isBookBundle)
    );
  }

  if (isSubscription) {
    // For subscriptions, look for subscription addons
    const isSubscriptionAddon =
      getAddonProperty('type') === 'Subscription Add-On';
    const isBookBundle = getAddonProperty('is_book_bundle') === 'true';

    return isSubscriptionAddon || isBookBundle;
  }

  return false;
}

/**
 * Determines the maximum quantity for a cart item based on product handle and quantity limitation
 * @param handle - The product handle
 * @param quantityLimitedToOne - Whether the item is quantity limited to one
 * @param parentAttributes - The parent cart item attributes to check for subscription
 * @returns The maximum quantity (1 or undefined)
 */
export function determineMaximumQuantity(
  handle: string,
  quantityLimitedToOne: string | undefined,
  parentAttributes: Array<{ key: string; value: string }> = [],
): number | undefined {
  // Array of handle patterns that should be limited to one quantity
  const quantityLimitedHandles = [
    'play-kits',
    'subscription-1',
    'course',
    'subscription-gifting',
  ];

  const isQuantityLimitedToOne = quantityLimitedToOne === 'true';
  const isHandleLimited = quantityLimitedHandles.some((pattern) =>
    handle.includes(pattern),
  );

  // Check if this is a book bundle with a subscription parent
  const isBookBundle = handle.includes('book-bundle');
  const isBookBundleWithSubscriptionParent =
    isBookBundle &&
    parentAttributes.some(
      (attr) =>
        attr.key === 'is_playgenius_subscription' && attr.value === 'true',
    );

  if (
    isHandleLimited ||
    isQuantityLimitedToOne ||
    isBookBundleWithSubscriptionParent
  ) {
    return 1;
  }

  return undefined;
}
